import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AuthLayout } from "@/components/AuthLayout";
import { Input, Label } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";

const btn = "w-full h-12 rounded-full bg-primary text-primary-foreground font-bold hover:bg-primary/90 disabled:opacity-60 transition-[background-color]";

export function ForgotPassword() {
  const { t } = usePrefs();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    api.get("/system/status").then((r) => setBlocked(r.data.providers.email.status === "blocked")).catch(() => {});
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/password/forgot", { email });
      setSent(true);
    } catch (err) {
      toast.error(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("forgot.title")} sub={t("forgot.sub")}>
      {sent ? (
        <p data-testid="forgot-sent" className="rounded-2xl bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 p-4 text-sm font-semibold">{t("forgot.sent")}</p>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
          <Label htmlFor="f-email">{t("auth.email")}</Label>
          <Input id="f-email" type="email" dir="ltr" required value={email} onChange={(e: any) => setEmail(e.target.value)} data-testid="forgot-email-input" className="h-12 rounded-2xl" />
          <button disabled={busy} data-testid="forgot-submit" className={btn}>{busy ? t("common.loading") : t("forgot.send")}</button>
        </form>
      )}
      {blocked && <p data-testid="forgot-provider-blocked" className="rounded-2xl bg-amber-500/15 text-amber-800 dark:text-amber-300 p-3 text-xs font-semibold">{t("forgot.blocked")}</p>}
      <Link to="/login" data-testid="forgot-back-link" className="text-sm font-bold text-primary">{t("auth.login")}</Link>
    </AuthLayout>
  );
}

export function ResetPassword() {
  const { t } = usePrefs();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/password/reset", { token, password });
      toast.success(t("reset.done"));
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("reset.title")} sub={t("reset.sub")}>
      {token.length < 20 ? (
        <p data-testid="reset-missing" className="rounded-2xl bg-accent p-4 text-sm font-bold">{t("reset.missing")}</p>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
          <Label htmlFor="r-pw">{t("auth.password")}</Label>
          <Input id="r-pw" type="password" dir="ltr" required minLength={8} value={password} onChange={(e: any) => setPassword(e.target.value)} data-testid="reset-password-input" className="h-12 rounded-2xl" />
          <p className="text-xs text-muted-foreground">{t("auth.pwHint")}</p>
          <button disabled={busy} data-testid="reset-submit" className={btn}>{busy ? t("common.loading") : t("reset.submit")}</button>
        </form>
      )}
    </AuthLayout>
  );
}
